import clsx from "clsx"

import {
  ActionType,
  ConditionType,
  ModifierType,
  PointRule,
  PrizePoolType,
  ReferralRewardType,
  Tournament,
  TournamentScoringMode,
  TournamentStatus
} from "types/tournament.types"

// ============================================================================
// Tournament status badge colours (UPPERCASE keys match Tournament.status)
// ============================================================================

export const STATUS_COLORS: Record<string, string> = {
  DRAFT: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  SCHEDULED: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  ACTIVE: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300",
  COMPLETED: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
  CANCELLED: "bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-300"
}

/** Resolve status colour, falling back to SCHEDULED style. Case-insensitive. */
export const getStatusColor = (status: string) => STATUS_COLORS[status.toUpperCase()] || STATUS_COLORS.SCHEDULED

/** Render a coloured status pill */
export const getStatusBadge = (status: string) => (
  <span className={clsx("px-2 py-1 text-xs font-medium rounded-full", getStatusColor(status))}>{status}</span>
)

// ============================================================================
// Tournament constants
// ============================================================================

export const TOURNAMENT_STATUS_OPTIONS: Array<{ value: TournamentStatus; label: string }> = [
  { value: "DRAFT", label: "Draft" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "ACTIVE", label: "Active" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" }
]

export const SCORING_MODE_OPTIONS: Array<{ value: TournamentScoringMode; label: string }> = [
  { value: "POINTS", label: "Points" },
  { value: "WAGER", label: "Wager Amount" },
  { value: "BET_COUNT", label: "Bet Count" }
]

export const PRIZE_POOL_TYPE_OPTIONS: Array<{ value: PrizePoolType | ""; label: string }> = [
  { value: "", label: "None" },
  { value: "FIXED", label: "Fixed" },
  { value: "PROGRESSIVE", label: "Progressive" }
]

export const REFERRAL_REWARD_TYPE_OPTIONS: Array<{ value: ReferralRewardType; label: string }> = [
  { value: "FIXED_PER_INVITE", label: "Fixed Points per Invite" },
  { value: "FIXED_PER_ACTION", label: "Fixed Points per Referee Spin" },
  { value: "PERCENTAGE", label: "% of Referee's Points" }
]

// ============================================================================
// Tournament list – filter options
// ============================================================================

export const statusOptions = [
  { name: "All Statuses", value: "" },
  ...TOURNAMENT_STATUS_OPTIONS.map((opt) => ({ name: opt.label, value: opt.value }))
]

export const scoringModeOptions = [
  { name: "All Modes", value: "" },
  ...SCORING_MODE_OPTIONS.map((opt) => ({ name: opt.label, value: opt.value }))
]

// ============================================================================
// Tournament list – row actions
// ============================================================================

interface TournamentActionHandlers {
  onView: (row: Tournament) => void
  onEdit: (row: Tournament) => void
  onMonitor: (row: Tournament) => void
  onPublish: (row: Tournament) => void
  onDuplicate: (row: Tournament) => void
  onStart: (row: Tournament) => void
  onEnd: (row: Tournament) => void
  onCancel: (row: Tournament) => void
  onDelete: (row: Tournament) => void
}

export const getTournamentActions = (h: TournamentActionHandlers) => [
  { name: "View Details", handler: h.onView },
  { name: "Edit", handler: h.onEdit, condition: (row: Tournament) => row.status === "DRAFT" || row.status === "SCHEDULED" },
  { name: "Monitor", handler: h.onMonitor, condition: (row: Tournament) => row.status === "ACTIVE" || row.status === "COMPLETED" },
  { name: "Duplicate", handler: h.onDuplicate },
  { name: "Publish", handler: h.onPublish, condition: (row: Tournament) => row.status === "DRAFT" },
  { name: "Start", handler: h.onStart, condition: (row: Tournament) => row.status === "SCHEDULED" },
  { name: "End", handler: h.onEnd, condition: (row: Tournament) => row.status === "ACTIVE" },
  {
    name: "Cancel",
    handler: h.onCancel,
    condition: (row: Tournament) => row.status === "DRAFT" || row.status === "SCHEDULED" || row.status === "ACTIVE",
    buttonColor: "red"
  },
  {
    name: "Delete",
    handler: h.onDelete,
    condition: (row: Tournament) => row.status === "CANCELLED",
    buttonColor: "red"
  }
]

// ============================================================================
// Dropdown options (used by PointRulesBuilder selects)
// ============================================================================

export const CONDITION_OPTIONS: { value: ConditionType; label: string; group: string }[] = [
  // Basic
  { value: "always", label: "Every spin (no condition)", group: "Basic" },
  { value: "any_win", label: "Any win", group: "Basic" },
  { value: "any_loss", label: "Any loss", group: "Basic" },
  // Streaks
  { value: "win_streak", label: "Win streak (N+ in a row)", group: "Streaks" },
  { value: "loss_streak", label: "Loss streak (N+ in a row)", group: "Streaks" },
  { value: "win_x_of_y", label: "Win X of last Y spins", group: "Streaks" },
  // Multiplier
  { value: "multiplier_gte", label: "Multiplier >= value", group: "Multiplier" },
  { value: "multiplier_lte", label: "Multiplier <= value", group: "Multiplier" },
  { value: "multiplier_between", label: "Multiplier in range", group: "Multiplier" },
  { value: "multiplier_exact", label: "Multiplier exact value", group: "Multiplier" },
  // Bet/Wager
  { value: "bet_gte", label: "Bet amount >= value", group: "Bet" },
  { value: "bet_lte", label: "Bet amount <= value", group: "Bet" },
  { value: "bet_lt", label: "Bet amount < value", group: "Bet" },
  { value: "win_gte", label: "Win amount >= value", group: "Bet" },
  { value: "win_lte", label: "Win amount <= value", group: "Bet" },
  { value: "total_wagers_gte", label: "Total wagers >= amount", group: "Bet" },
  { value: "total_wins_gte", label: "Total wins >= count", group: "Bet" },
  // Activity
  { value: "spin_count_gte", label: "Total spins >= count", group: "Activity" },
  { value: "spins_in_timeframe", label: "N spins within M minutes", group: "Activity" },
  { value: "inactivity", label: "Inactive for N minutes", group: "Activity" },
  // Game
  { value: "playing_game", label: "Playing specific game(s)", group: "Game" },
  { value: "playing_category", label: "Playing game category", group: "Game" },
  // Time
  { value: "time_window", label: "During time window (hours)", group: "Time" },
  { value: "first_minutes", label: "First N minutes of tournament", group: "Time" },
  { value: "last_minutes", label: "Last N minutes of tournament", group: "Time" },
  // Ranking
  { value: "reached_rank", label: "Reached rank N or better", group: "Ranking" },
  { value: "overtook_player", label: "Overtook another player", group: "Ranking" },
  { value: "got_overtaken", label: "Got overtaken by player", group: "Ranking" }
]

export const ACTION_OPTIONS: { value: ActionType; label: string; group: string }[] = [
  // Points
  { value: "award_points", label: "Award fixed points", group: "Points" },
  { value: "award_bet_percentage", label: "Award % of bet amount", group: "Points" },
  { value: "award_win_percentage", label: "Award % of win amount", group: "Points" },
  { value: "award_multiplier_bonus", label: "Award multiplier x value", group: "Points" },
  // Penalties
  { value: "lose_points", label: "Lose fixed points", group: "Penalties" },
  { value: "lose_percentage", label: "Lose % of total points", group: "Penalties" },
  // Multipliers
  { value: "multiply_next_spins", label: "Multiply next N spins", group: "Multipliers" },
  { value: "multiply_for_duration", label: "Multiply for N minutes", group: "Multipliers" },
  // PvP
  { value: "steal_points", label: "Steal fixed points from nearby", group: "PvP" },
  { value: "steal_percentage", label: "Steal % from nearby players", group: "PvP" },
  // Defense
  { value: "shield", label: "Add shield (protect from steal)", group: "Defense" },
  { value: "point_lock", label: "Lock points from being stolen", group: "Defense" },
  // Special
  { value: "eliminate_all", label: "MOAB - reduce all players' points", group: "Special" }
]

export const MODIFIER_OPTIONS: { value: ModifierType; label: string }[] = [
  { value: "no_limit", label: "No limit" },
  { value: "one_time", label: "One time only" },
  { value: "max_times", label: "Max N times total" },
  { value: "max_per_hour", label: "Max N times per hour" },
  { value: "max_per_day", label: "Max N times per day" }
]

// ============================================================================
// Short labels for read-only display
// ============================================================================

export const CONDITION_LABELS: Record<string, string> = {
  always: "Every Spin",
  any_win: "Any Win",
  any_loss: "Any Loss",
  win_streak: "Win Streak",
  loss_streak: "Loss Streak",
  win_x_of_y: "Win X of Y",
  multiplier_gte: "Multiplier >=",
  multiplier_lte: "Multiplier <=",
  multiplier_between: "Multiplier Between",
  multiplier_exact: "Multiplier Exact",
  bet_gte: "Bet >=",
  bet_lte: "Bet <=",
  bet_lt: "Bet <",
  win_gte: "Win >=",
  win_lte: "Win <=",
  total_wagers_gte: "Total Wagers >=",
  total_wins_gte: "Total Wins >=",
  spin_count_gte: "Spin Count >=",
  spins_in_timeframe: "Spins in Timeframe",
  inactivity: "Inactivity",
  time_window: "Time Window",
  first_minutes: "First N Minutes",
  last_minutes: "Last N Minutes",
  reached_rank: "Reached Rank",
  overtook_player: "Overtook Player",
  got_overtaken: "Got Overtaken",
  playing_game: "Playing Game",
  playing_category: "Playing Category"
}

export const ACTION_LABELS: Record<string, string> = {
  award_points: "Award Points",
  award_bet_percentage: "Award % of Bet",
  award_win_percentage: "Award % of Win",
  award_multiplier_bonus: "Award Multiplier Bonus",
  lose_points: "Lose Points",
  lose_percentage: "Lose % of Points",
  steal_points: "Steal Points",
  steal_percentage: "Steal %",
  shield: "Shield",
  point_lock: "Lock Points",
  multiply_next_spins: "Multiply Next Spins",
  multiply_for_duration: "Multiply for Duration",
  eliminate_all: "MOAB"
}

export const MODIFIER_LABELS: Record<string, string> = {
  no_limit: "No Limit",
  one_time: "One Time",
  max_times: "Max Times",
  max_per_hour: "Max / Hour",
  max_per_day: "Max / Day"
}

// ============================================================================
// Helpers
// ============================================================================

/** Look up a short label, falling back to the raw type string */
export const formatLabel = (map: Record<string, string>, type?: string): string => (type ? map[type] || type : "N/A")

/** Render <optgroup> elements from grouped options */
export function groupedOptions(options: { value: string; label: string; group: string }[]) {
  const groups: Record<string, typeof options> = {}
  for (const opt of options) {
    if (!groups[opt.group]) groups[opt.group] = []
    groups[opt.group].push(opt)
  }

  return Object.entries(groups).map(([group, opts]) => (
    <optgroup key={group} label={group}>
      {opts.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </optgroup>
  ))
}

// ============================================================================
// Field descriptors for read-only rule display
// ============================================================================

type FieldDef<T> = { key: string; label: string; format?: (v: any, obj: T) => string; color?: string }

export const conditionFields: FieldDef<PointRule["if"]>[] = [
  { key: "value", label: "Value", format: (v) => `${v}x` },
  { key: "min", label: "Min", format: (v) => `${v}x` },
  { key: "max", label: "Max", format: (v) => `${v}x` },
  { key: "amount", label: "Amount", format: (v) => `$${(Number(v) / 100).toFixed(2)}` },
  { key: "count", label: "Count" },
  { key: "wins", label: "Wins" },
  { key: "spins", label: "Spins" },
  { key: "minutes", label: "Minutes" },
  { key: "rank", label: "Rank", format: (v) => `#${v}` },
  { key: "game_ids", label: "Games", format: (v) => (Array.isArray(v) && v.length > 0 ? `${v.length} game(s)` : "") },
  { key: "category", label: "Category" }
]

export const actionFields: FieldDef<PointRule["then"]>[] = [
  { key: "amount", label: "Amount", color: "text-green-600 dark:text-green-400" },
  { key: "percentage", label: "Percentage", format: (v) => `${v}%`, color: "text-blue-600 dark:text-blue-400" },
  { key: "multiplier", label: "Multiplier", format: (v) => `${v}x`, color: "text-purple-600 dark:text-purple-400" },
  { key: "spin_count", label: "Spins" },
  { key: "duration_minutes", label: "Duration", format: (v) => `${v} min` },
  { key: "target_count", label: "Targets", format: (v) => `${v} player(s)` }
]

export const modifierFields: FieldDef<NonNullable<PointRule["modifier"]>>[] = [
  { key: "count", label: "Limit", color: "text-orange-600 dark:text-orange-400" }
]

/** Render field rows for a rule section, skipping undefined / empty values */
export const renderFields = <T extends Record<string, any>>(obj: T | undefined, fields: FieldDef<T>[]) =>
  fields.map(({ key, label, format, color }) => {
    const val = obj?.[key]
    if (val === undefined || val === null) return null
    if (Array.isArray(val) && val.length === 0) return null
    const display = format ? format(val, obj as T) : String(val)
    if (!display) return null
    return (
      <div key={key}>
        {label}: <span className={`font-medium ${color || ""}`}>{display}</span>
      </div>
    )
  })
